import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useSetRecoilState, useRecoilState } from 'recoil';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@apollo/client';

import WarningCard from '@/components/commons/warning_card';
import Title from '@/components/commons/page-elements/Title';
import { timezones, getLocalTimezone } from '@/hooks/calendar_helpers';
import Loader from '@/components/commons/loader';
import Page from '@/components/layout/page';
import BirthdaysForm from '@/components/calendar/birthdays/BirthdaysForm';
import {
    handleValidationErrors,
    handleFetchErrors,
    onErrorHandler,
    onCompletedHandler,
} from '@/services/apollo';
import { withAuth } from '@/components/with_auth';
import { updateBirthdaySettings, SEND_BIRTHDAY } from '@/components/settings/Actions';
import { initializedAtom, userAtom } from '@/components/with_auth/State';
import ConfirmActionModal from '@/components/settings/ConfirmActionModal';
import styles from '@/components/settings/Settings.module.css';

import IgnoreToggle from '@/components/calendar/birthdays/IgnoreToggle';
import { FETCH_BIRTHDAY_IGNORED_CONTACTS, UPDATE_BIRTHDAY_IGNORED_CONTACTS } from '@/components/calendar/birthdays/Actions';

function Birthdays() {
    const setInitialized = useSetRecoilState(initializedAtom);
    const [user, setUser] = useRecoilState(userAtom);
    const setters = {
        setUser,
        setInitialized,
    };

    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [isError, setIsError] = useState(false);
    const [formError, setFormError] = useState('');

    const [sendBirthdayMailNow] = useMutation(SEND_BIRTHDAY);

    const [birthdayIgnoredContacts, setBirthdayIgnoredContacts] = useState([]);

    const { loading: loadingContacts } = useQuery(FETCH_BIRTHDAY_IGNORED_CONTACTS, {
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (data) => {
            const contactsData = data?.user?.unifiedContacts;
            setBirthdayIgnoredContacts(contactsData);
        }),
    });

    useEffect(() => {
        if (isError) {
            setFormError('An unexpected error occurred');
            setIsError(false);
        }
    }, [isError]);

    const handleUpdateUser = async (
        birthdayEmailConfig: {[key: string]: any},
        cb: (data: any, error: any) => void,
    ) => {
        await updateBirthdaySettings(
            birthdayEmailConfig,
            setters,
            cb,
        );
    };

    const sendBirthday = async () => {
        setIsSending(true);
        await sendBirthdayMailNow({
            onCompleted: onCompletedHandler(setIsError, (data) => {
                const errors = data?.sendBirthdayMailNow?.errors;
                if (errors && errors.length > 0) {
                    setFormError('An unexpected error occurred');
                } else {
                    toast.success('Successfully sent birthday list emails.');
                    if (formError !== '') setFormError('');
                }
            }),
            onError: onErrorHandler(setIsError),
        });
        setIsSending(false);
    };

    const [modalState, setModalState] = useState({ show: false, action: null });

    const submitAction = async () => { };

    useEffect(() => {
        if (user) {
            const {
                birthday,
                timezoneName,
                timezoneOffset,
            } = user;
            let timezoneIndex = null;
            for (let i = 0; i < timezones.length; i++) {
                if (timezones[i][0] === timezoneName && timezones[i][1] === timezoneOffset) {
                    timezoneIndex = i;
                    break;
                }
            }
            if (!timezoneIndex) {
                timezoneIndex = getLocalTimezone();
            }

            let day = '';
            let month = '';
            if (birthday) {
                const rawDate = new Date(birthday);
                const rawDay = rawDate.getUTCDate();
                day = rawDay <= 9 ? `0${rawDay}` : `${rawDay}`;
                const rawMonth = rawDate.getUTCMonth() + 1;
                month = rawMonth <= 9 ? `0${rawMonth}` : `${rawMonth}`;
            }

            setUserDetails({
                day: day || '',
                month: month || '',
                timezone: timezoneIndex || '',
            });
            setLoading(false);
        }
    }, [user]);

    const [updateBirthdayIgnore] = useMutation(UPDATE_BIRTHDAY_IGNORED_CONTACTS);
    const handleBirthdayIgnore = async (id, ignoreBirthday, _) => {
        try {
            setLoading(true);
            const response = await updateBirthdayIgnore({
                variables: {
                    updateUnifiedContactInput: {
                        id,
                        attributes: {
                            ignoreBirthday,
                        },
                    },
                },
            });

            if (!response.data.updateUnifiedContact.unifiedContact.errors) {
                if (formError !== '') setFormError('');

                toast.success('Contact was successfully removed from Ignore list.');
                const updatedBirthdayIgnoredContacts = birthdayIgnoredContacts.filter(
                    (obj) => obj.id !== response.data.updateUnifiedContact.unifiedContact.id,
                );
                setBirthdayIgnoredContacts(updatedBirthdayIgnoredContacts);
                setLoading(false);

                return;
            }

            setFormError('Something went wrong.  Please try again.');
            setLoading(false);
        } catch (err) {
            setLoading(false);
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    return (
        <Page>
            <div className="flex flex-wrap fullHeightLessNavPage bg-lightGray bg-opacity-30 items-start">
                <div className="w-full p-4 pt-8 sm:p-8">
                    <div className="flex justify-between relative flex-col md:flex-row">
                        <div className="pr-6 text-left ">
                            <Title
                                title="Outreach"
                                subtitle="Sixty will remind you to maintain connections based on your relationship importance. (Today we do this with birthday reminders, but stay tuned for other upcoming features.)"
                            />
                        </div>
                    </div>

                    {
                        formError !== '' && (
                            <div className="w-full mt-[20px] -mb-[30px]">
                                <WarningCard message={formError} />
                            </div>
                        )
                    }

                    <Loader isLoading={loading || !userDetails}>
                        <div className={`${styles.container} flex flex-col gap-0 p-0 mt-[60px] shadow-lg`}>
                            <div className="p-5">
                                {userDetails && (
                                    <Formik
                                        enableReinitialize
                                        initialValues={{
                                            birthdayEmailFrequency: user?.birthdayEmailFrequency || 'monthly',
                                            // eslint-disable-next-line max-len
                                            birthdayEmailMaxBirthdays: user?.birthdayEmailMaxBirthdays || 0,
                                            // eslint-disable-next-line max-len
                                            birthdayEmailReminderDays: user?.birthdayEmailReminderDays || 1,
                                            birthdayEmailContactType: user?.birthdayEmailContactType || 'both',
                                            birthdayEmailSort: user?.birthdayEmailSort || 'date',
                                        }}
                                        onSubmit={async (frm, { setSubmitting, setStatus }) => {
                                            await handleUpdateUser(
                                                frm,
                                                (data, error) => {
                                                    if (data) {
                                                        const { errors } = data.updateUser;
                                                        if (errors && errors.length > 0) {
                                                            handleValidationErrors(errors, setStatus, 'user');
                                                        } else {
                                                            setStatus({ errors: {} });
                                                            toast.success('Birthdays settings updated successfully.');
                                                            if (formError !== '') setFormError('');
                                                        }
                                                        setIsSending(false);
                                                        setSubmitting(false);
                                                    } else {
                                                        setSubmitting(false);
                                                        setIsSending(false);
                                                        handleFetchErrors(error);
                                                        setFormError('An unexpected error occurred.  Please try again later.');
                                                    }
                                                },
                                            );
                                        }}
                                    >
                                        {({
                                            status,
                                            isSubmitting,
                                        }) => (
                                            <BirthdaysForm
                                                status={status}
                                                isSubmitting={isSubmitting}
                                                isSendingMail={isSending}
                                                sendBirthdayMailNow={sendBirthday}
                                            />
                                        )}
                                    </Formik>
                                )}
                            </div>
                            <Loader isLoading={loadingContacts}>
                                {birthdayIgnoredContacts && birthdayIgnoredContacts.length > 0
                                     && (
                                         <div className="bg-gray-200 p-5 flex flex-col gap-2">
                                             <h3 className="text-[16px] font-semibold text-cleanSlate">Ignore list</h3>
                                             {birthdayIgnoredContacts.map((contact) => (
                                                 <IgnoreToggle
                                                     key={contact.id}
                                                     record={contact}
                                                     onChange={handleBirthdayIgnore}
                                                 />
                                             ))}
                                         </div>
                                     )}
                            </Loader>
                        </div>

                    </Loader>

                    <ConfirmActionModal
                        display={modalState.show}
                        onSubmit={submitAction}
                        closeModal={() => { setModalState({ show: false, action: null }); }}
                        actionType={modalState.action}
                    />
                </div>
            </div>
        </Page>
    );
}

export default withAuth(Birthdays);
